import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar, Phone, Mail, Globe, MapPin, MessageSquare } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOutreachContacts, useUpdateOutreachContact } from "@/hooks/useOutreach";
import { OutreachContact } from "@/types/outreach";
import { AddOutreachContactDialog } from "./AddOutreachContactDialog";
import { OutreachContactDetailModal } from "./OutreachContactDetailModal";
import { format } from "date-fns";

const OutreachManagement = () => {
  const { data: contacts, isLoading } = useOutreachContacts();
  const updateContactMutation = useUpdateOutreachContact();
  const [selectedContact, setSelectedContact] = useState<OutreachContact | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredContacts = contacts?.filter(contact => 
    statusFilter === 'all' || contact.status === statusFilter
  ) || [];

  const handleStatusUpdate = async (contactId: string, newStatus: string) => {
    await updateContactMutation.mutateAsync({
      id: contactId,
      status: newStatus as OutreachContact['status']
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_contacted':
        return 'bg-gray-100 text-gray-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'follow_up_needed':
        return 'bg-yellow-100 text-yellow-800';
      case 'interested':
        return 'bg-green-100 text-green-800';
      case 'not_interested':
        return 'bg-red-100 text-red-800';
      case 'signed_up':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'not_contacted':
        return 'Not Contacted';
      case 'contacted':
        return 'Contacted';
      case 'follow_up_needed':
        return 'Follow Up Needed';
      case 'interested':
        return 'Interested';
      case 'not_interested':
        return 'Not Interested';
      case 'signed_up':
        return 'Signed Up';
      default:
        return status.replace('_', ' ');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const statusCounts = contacts?.reduce((acc, contact) => {
    acc[contact.status] = (acc[contact.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const handleRowClick = (contact: OutreachContact) => {
    setSelectedContact(contact);
    setShowDetailModal(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Outreach Management</h2>
          <p className="text-gray-600">Track and manage wellness center outreach efforts</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card 
          className={`cursor-pointer transition-colors ${statusFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{contacts?.length || 0}</div>
            <div className="text-sm text-gray-600">Total Contacts</div>
          </CardContent>
        </Card>
        
        {[
          { key: 'not_contacted', label: 'Not Contacted', color: 'text-gray-600' },
          { key: 'contacted', label: 'Contacted', color: 'text-blue-600' },
          { key: 'follow_up_needed', label: 'Follow Up', color: 'text-yellow-600' },
          { key: 'interested', label: 'Interested', color: 'text-green-600' },
          { key: 'signed_up', label: 'Signed Up', color: 'text-purple-600' },
        ].map(({ key, label, color }) => (
          <Card 
            key={key}
            className={`cursor-pointer transition-colors ${statusFilter === key ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setStatusFilter(key)}
          >
            <CardContent className="p-4 text-center">
              <div className={`text-2xl font-bold ${color}`}>{statusCounts[key] || 0}</div>
              <div className="text-sm text-gray-600">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contacts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Outreach Contacts</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">No contacts found</div>
              <Button onClick={() => setShowAddDialog(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add your first contact
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Contact Info</TableHead>
                  <TableHead className="w-[160px]">Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Last Contact</TableHead>
                  <TableHead>Follow Up</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow 
                    key={contact.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(contact)}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{contact.business_name}</div>
                        {contact.contact_person && (
                          <div className="text-sm text-gray-500">{contact.contact_person}</div>
                        )}
                        {contact.location && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {contact.location}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {contact.email && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-3 w-3 mr-1" />
                            <span className="truncate max-w-[150px]">{contact.email}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="h-3 w-3 mr-1" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                        {contact.website && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Globe className="h-3 w-3 mr-1" />
                            <span className="truncate max-w-[150px]">{contact.website}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={contact.status}
                        onValueChange={(value) => handleStatusUpdate(contact.id, value)}
                        disabled={updateContactMutation.isPending}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white z-50">
                          <SelectItem value="not_contacted">Not Contacted</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="follow_up_needed">Follow Up Needed</SelectItem>
                          <SelectItem value="interested">Interested</SelectItem>
                          <SelectItem value="not_interested">Not Interested</SelectItem>
                          <SelectItem value="signed_up">Signed Up</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getPriorityColor(contact.priority || 'medium')}>
                        {contact.priority || 'medium'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {format(new Date(contact.last_contact_date), 'MMM dd, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.next_follow_up_date ? (
                        <div className="flex items-center text-sm text-orange-600">
                          <Calendar className="h-3 w-3 mr-1" />
                          {format(new Date(contact.next_follow_up_date), 'MMM dd')}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.notes ? (
                        <div className="flex items-center text-sm text-gray-600">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          <span className="truncate max-w-[200px]">{contact.notes}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AddOutreachContactDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />

      <OutreachContactDetailModal
        contact={selectedContact}
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
      />
    </div>
  );
};

export default OutreachManagement;
